import UnitPageShell from '../components/shared/UnitPageShell';
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  IconButton,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  InputAdornment,
  Container,
  Avatar,
  Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WhatsAppNumberDialog from '../components/WhatsAppNumberDialog';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import { parseScore } from '../types/user';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { API_ENDPOINTS, resolveAssetUrl } from '../utils/apiConfig';
import { tokens } from '../theme/tokens';
import { useTheme as useThemeContext } from '../contexts/ThemeContext';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  light_admin: 'Light Admin',
  user_admin: 'User Admin',
  team_admin: 'Team Admin',
  role_admin: 'Role Admin',
  updates_admin: 'Updates Admin',
  quiz_admin: 'Quiz Admin',
  docs_admin: 'Docs Admin',
  phonebook_admin: 'Phonebook Admin',
  faq_admin: 'FAQ Admin',
  dashboard_admin: 'Dashboard Admin',
  highscore_admin: 'Highscore Admin',
  content_admin: 'Content Admin',
  editor_1: 'Editor L1',
  editor_2: 'Editor L2',
  editor_3: 'Editor L3',
  editor_4: 'Editor L4',
  editor_5: 'Editor L5',
  editor_6: 'Editor L6',
  editor_7: 'Editor L7',
  editor_8: 'Editor L8',
  editor_9: 'Editor L9',
  editor_10: 'Editor L10',
  creator_1: 'Creator L1',
  creator_2: 'Creator L2',
  creator_3: 'Creator L3',
  creator_4: 'Creator L4',
  creator_5: 'Creator L5',
  creator_6: 'Creator L6',
  creator_7: 'Creator L7',
  creator_8: 'Creator L8',
  creator_9: 'Creator L9',
  creator_10: 'Creator L10',
  aussendienst: 'Aussendienst',
  inbetriebnehmer: 'Inbetriebnehmer',
  programmierer: 'Programmierer',
  cad: 'CAD',
  trainer: 'Trainer',
  support: 'Support',
  qa: 'QA',
  user: 'User'
};

const Profile: React.FC = () => {
  const { darkMode } = useThemeContext();
  const {

    user,
    token,
    refreshProfile,
    totalScore,
    roles,
    role,
    canManageUsers,
    canManageTeams,
    canManageUpdates,
    canManageQuiz,
    canManageFaq,
    canManageDocuments,
    canManagePhonebook,
    canManageDashboard,
    canManageHighscore,
    canEditContent,
    canManageProfile
  } = useAuth();
  
  // Profil-Daten
  const [alias, setAlias] = useState(user?.alias || '');
  const [eventNumber, setEventNumber] = useState(user?.event || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  
  // Passwort-Daten
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  
  const [waDialogOpen, setWaDialogOpen] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [profileImageUploading, setProfileImageUploading] = useState(false);
  const [profileImageMessage, setProfileImageMessage] = useState<string | null>(null);
  const profileImageInputRef = React.useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setAlias(user?.alias || '');
    setEventNumber(user?.event || '');
    setProfileImage(resolveAssetUrl(user?.profile_image ?? null));
  }, [user]);

  useEffect(() => {
    if (!profileImageFile) return;
    const objectUrl = URL.createObjectURL(profileImageFile);
    setProfileImage(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [profileImageFile]);

  const handleSaveProfile = async () => {
    setProfileError(null);
    setProfileMessage(null);
    if (!token) { 
      setProfileError('Nicht authentifiziert'); 
      return; 
    }
    
    setProfileLoading(true);
    try {
      const payload: Record<string, any> = {};
      if (alias !== user?.alias) payload.alias = alias;
      if (eventNumber !== user?.event) payload.event = eventNumber;
      
      if (Object.keys(payload).length === 0) {
        setProfileMessage('Keine Änderungen.');
        return;
      }
      
      const res = await authService.updateUser(token, payload);
      if (res?.success === false) {
        setProfileError(res.message || 'Änderung fehlgeschlagen');
      } else {
        setProfileMessage(res.message || 'Profil erfolgreich aktualisiert');
        await refreshProfile();
      }
    } catch (e: any) {
      setProfileError(e?.error || e?.message || 'Fehler beim Speichern');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordMessage(null);
    
    if (!token) {
      setPasswordError('Nicht authentifiziert');
      return;
    }
    
    // Validierung
    if (!newPassword || newPassword.length < 6) {
      setPasswordError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwörter stimmen nicht überein');
      return;
    }
    
    setPasswordLoading(true);
    try {
      const res = await authService.changePassword(token, newPassword);
      if (res?.success === false) {
        setPasswordError(res.error || res.message || 'Passwort konnte nicht geändert werden');
      } else {
        setPasswordMessage(res.message || 'Passwort erfolgreich geändert');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (e: any) {
      setPasswordError(e?.error || e?.message || 'Fehler beim Ändern des Passworts');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleProfileImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setProfileImageFile(file);
    setProfileImageMessage(null);
  };

  const handleProfileImageUpload = async () => {
    if (!token) {
      setProfileImageMessage('Nicht authentifiziert');
      return;
    }
    if (!profileImageFile) {
      setProfileImageMessage('Bitte zuerst ein Bild auswaehlen.');
      return;
    }

    const formData = new FormData();
    formData.append('action', 'uploadProfileImage');
    formData.append('image_file', profileImageFile);

    setProfileImageUploading(true);
    try {
      const response = await fetch(API_ENDPOINTS.api, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Upload fehlgeschlagen');
      }
      setProfileImage(resolveAssetUrl(data.image));
      setProfileImageFile(null);
      if (profileImageInputRef.current) profileImageInputRef.current.value = '';
      setProfileImageMessage('Profilbild gespeichert.');
      await refreshProfile();
    } catch (e: any) {
      setProfileImageMessage(e?.message || 'Upload fehlgeschlagen');
    } finally {
      setProfileImageUploading(false);
    }
  };

  if (!user) {
    return (
      <UnitPageShell title="Profil">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </UnitPageShell>
    );
  }

  if (!canManageProfile) {
    return (
      <UnitPageShell title="Zugriff verweigert">
        <Container maxWidth={false} disableGutters sx={{ py: 6, px: { xs: 2, md: 4 } }}>
          <Alert severity="error">Keine Berechtigung fuer Profilfunktionen.</Alert>
        </Container>
      </UnitPageShell>
    );
  }

  // Sammle Quiz-Scores
  const quizKeys = Object.keys(user).filter(k => k.startsWith('quiz_'));

  return (
    <UnitPageShell title="Mein Profil">
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Mein Profil
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Benutzerdaten Card */}
        <Box>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Benutzerdaten
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2} sx={{ mb: 2 }}>
                <Avatar
                  src={profileImage || undefined}
                  sx={{ width: 96, height: 96, bgcolor: 'grey.200' }}
                >
                  {user.alias?.[0] || user.email?.[0] || 'U'}
                </Avatar>
                <input
                  ref={profileImageInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleProfileImageChange}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    onClick={() => profileImageInputRef.current?.click()}
                  >
                    Bild auswaehlen
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleProfileImageUpload}
                    disabled={profileImageUploading || !profileImageFile}
                  >
                    {profileImageUploading ? 'Lade hoch...' : 'Profilbild speichern'}
                  </Button>
                </Stack>
                {profileImageMessage && (
                  <Alert severity={profileImageMessage.includes('fehl') ? 'error' : 'success'}>
                    {profileImageMessage}
                  </Alert>
                )}
              </Stack>

              <Box sx={{ mb: 2 }}>
                <TextField
                  label="E-Mail"
                  fullWidth
                  value={user.email}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Alias / Anzeigename"
                  fullWidth
                  value={alias}
                  onChange={(e) => setAlias(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  label="WhatsApp-Nummer"
                  fullWidth
                  value={eventNumber || ''}
                  onChange={(e) => setEventNumber(e.target.value)}
                  placeholder="+49..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="WhatsApp Hilfe"
                          onClick={() => setWaDialogOpen(true)}
                          edge="end"
                        >
                          <EditIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Für Passwort-Rücksetzung via WhatsApp
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Zugeordnete Rollen
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {(roles && roles.length > 0 ? roles : [role || user.role || 'user'])
                    .filter(Boolean)
                    .map((roleKey) => (
                      <Chip
                        key={roleKey}
                        label={ROLE_LABELS[String(roleKey)] || String(roleKey)}
                        size="small"
                        color="primary"
                        sx={{ mb: 1 }}
                      />
                    ))}
                </Stack>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Deine aktuellen Rechte
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {[
                    canManageProfile && 'Profil verwalten',
                    canManageUsers && 'Benutzerverwaltung',
                    canManageTeams && 'Teamverwaltung',
                    canManageUpdates && 'Updates verwalten',
                    canManageQuiz && 'Quiz verwalten',
                    canManageFaq && 'FAQ verwalten',
                    canManageDocuments && 'Dokumente verwalten',
                    canManagePhonebook && 'Telefonliste verwalten',
                    canManageDashboard && 'Dashboard verwalten',
                    canManageHighscore && 'Highscore verwalten',
                    canEditContent && 'Inhalte bearbeiten'
                  ]
                    .filter(Boolean)
                    .map((label) => (
                      <Chip key={String(label)} label={String(label)} size="small" sx={{ mb: 1 }} />
                    ))}
                  {[
                    canManageUsers,
                    canManageTeams,
                    canManageUpdates,
                    canManageQuiz,
                    canManageFaq,
                    canManageDocuments,
                    canManagePhonebook,
                    canManageDashboard,
                    canManageHighscore,
                    canEditContent
                  ].every((value) => !value) && (
                    <Typography variant="body2" color="text.secondary">
                      Keine speziellen Rechte.
                    </Typography>
                  )}
                </Stack>
              </Box>

              {profileMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {profileMessage}
                </Alert>
              )}
              {profileError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {profileError}
                </Alert>
              )}

              <Button
                variant="contained"
                fullWidth
                onClick={handleSaveProfile}
                disabled={profileLoading}
                startIcon={profileLoading ? <CircularProgress size={20} /> : <PersonIcon />}
              >
                {profileLoading ? 'Speichern...' : 'Profil speichern'}
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Passwort ändern Card */}
        <Box>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Passwort ändern
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Neues Passwort"
                  fullWidth
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Passwort anzeigen"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Mindestens 6 Zeichen
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Passwort bestätigen"
                  fullWidth
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Passwort anzeigen"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {passwordMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {passwordMessage}
                </Alert>
              )}
              {passwordError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {passwordError}
                </Alert>
              )}

              <Button
                variant="contained"
                fullWidth
                onClick={handleChangePassword}
                disabled={passwordLoading || !newPassword || !confirmPassword}
                startIcon={passwordLoading ? <CircularProgress size={20} /> : <LockIcon />}
                color="secondary"
              >
                {passwordLoading ? 'Ändern...' : 'Passwort ändern'}
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Quiz-Statistiken Card */}
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmojiEventsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Quiz-Statistiken
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center' }}>
                  {totalScore}
                </Typography>
                <Typography variant="body1" sx={{ textAlign: 'center' }}>
                  Gesamtpunkte
                </Typography>
              </Box>

              {quizKeys.length === 0 ? (
                <Alert severity="info">
                  Noch keine Quiz-Daten vorhanden. Starte dein erstes Quiz!
                </Alert>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                  {quizKeys.map((k) => {
                    const score = parseScore((user as any)[k]);
                    const quizName = k.replace('quiz_', '').replace(/_/g, ' ').toUpperCase();
                    return (
                      <Box key={k}>
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            textAlign: 'center',
                            bgcolor: 'background.default',
                            borderLeft: 3,
                            borderColor: score > 0 ? 'success.main' : 'grey.300',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {quizName}
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 600, color: score > 0 ? 'success.main' : 'text.secondary' }}>
                            {score}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Punkte
                          </Typography>
                        </Paper>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

        <WhatsAppNumberDialog open={waDialogOpen} onClose={() => setWaDialogOpen(false)} />
      </Container>
    </UnitPageShell>
  );
};

export default Profile;
